<template>
  <div class="w-full flex">
    <!-- Toggle Button -->
    <div v-if="!showForm">
      <button
        @click="openWorkLogger"
        class="w-full flex items-center gap-3 px-3 py-2.5 rounded text-left hover:bg-gray-50 transition-colors"
      >
        <div class="w-7 h-7 rounded bg-primary-50 flex items-center justify-center shrink-0">
          <FontAwesomeIcon :icon="['fas', 'plus']" class="text-xs text-primary-600" />
        </div>
        <div>
          <span class="text-xs font-medium text-gray-900">Log Work</span>
          <p class="text-xs text-gray-400">{{ mode === 'patient' ? 'Record work for this patient' : 'Record your work on this task' }}</p>
        </div>
      </button>
    </div>

    <!-- Work Logger Form -->
    <div v-if="showForm" class="w-full bg-white rounded p-3">
      <div class="flex items-center justify-between mb-3">
        <span class="text-xs font-medium text-gray-900">Log Work Activity</span>
        <button @click="closeWorkLogger" class="text-gray-400 hover:text-gray-500">
          <FontAwesomeIcon :icon="['fas', 'times']" class="text-xs" />
        </button>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="py-4 text-center">
        <FontAwesomeIcon :icon="['fas', 'spinner']" class="animate-spin text-gray-400" />
      </div>

      <!-- Opportunity Selection (Patient mode only) -->
      <div v-else-if="mode === 'patient' && !selectedWorkType" class="space-y-2">
        <div v-if="loadingOpportunities" class="py-3 text-center">
          <FontAwesomeIcon :icon="['fas', 'spinner']" class="animate-spin text-gray-400" />
        </div>
        <div v-else>
          <div v-if="patientOpportunities.length === 0" class="text-center py-3">
            <p class="text-xs text-gray-400">No active or recently-completed opportunities found</p>
          </div>
          <div v-else>
            <label class="block text-xs text-gray-500 mb-1.5">Select Opportunity</label>
            <div class="relative" ref="oppDropdownContainer">
              <button
                @click="showOppDropdown = !showOppDropdown"
                class="w-full flex items-center justify-between px-3 py-2 text-xs rounded border transition-colors"
                :class="selectedOpportunityId
                  ? 'border-primary-300 bg-primary-50 text-primary-700 font-medium'
                  : 'border-gray-200 text-gray-500 hover:border-gray-300'"
              >
                <span>{{ selectedOpportunityId ? selectedOpportunityLabel : 'Choose an opportunity...' }}</span>
                <FontAwesomeIcon :icon="['fas', 'chevron-down']" class="text-[9px]" :class="showOppDropdown ? 'rotate-180' : ''" style="transition: transform 0.15s;" />
              </button>
              <div v-if="showOppDropdown" class="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded shadow-lg overflow-hidden">
                <button
                  v-for="opp in patientOpportunities"
                  :key="opp.id"
                  @click="selectedOpportunityId = opp.id; showOppDropdown = false"
                  class="w-full text-left px-3 py-2 text-xs transition-colors"
                  :class="opp.id === selectedOpportunityId
                    ? 'bg-primary-50 text-primary-700 font-medium'
                    : 'text-gray-700 hover:bg-gray-50'"
                >
                  {{ opp.label }}
                </button>
              </div>
            </div>
            <div v-if="selectedOpportunityTaskTypeName" class="flex items-center gap-1.5 mt-1.5 px-1">
              <FontAwesomeIcon :icon="['fas', 'circle']" class="text-[5px] text-primary-400" />
              <span class="text-xs text-gray-500">Current stage: <span class="font-medium text-gray-700">{{ selectedOpportunityTaskTypeName }}</span></span>
            </div>
            <div v-if="selectedOpportunityId" class="mt-3">
              <div v-if="loading" class="py-3 text-center">
                <FontAwesomeIcon :icon="['fas', 'spinner']" class="animate-spin text-gray-400" />
              </div>
              <div v-else-if="availableWorkTypes.length > 0" class="space-y-1">
                <label class="block text-xs text-gray-500 mb-1.5">Select Work Type</label>
                <button
                  v-for="workType in availableWorkTypes"
                  :key="workType.id"
                  @click="selectWorkType(workType)"
                  class="w-full text-left px-3 py-2.5 rounded-lg border border-gray-200 hover:border-primary-300 hover:bg-primary-50 hover:shadow-sm transition-all group"
                >
                  <div class="flex items-center justify-between">
                    <span class="text-xs font-medium text-gray-900 group-hover:text-primary-700">{{ workType.display_name || workType.name }}</span>
                    <FontAwesomeIcon :icon="['fas', 'chevron-right']" class="text-[10px] text-gray-300 group-hover:text-primary-500" />
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Work Type Selection (task/opportunity/legacy modes) -->
      <div v-else-if="!selectedWorkType" class="space-y-1">
        <label class="block text-xs text-gray-500 mb-1">Select Work Type</label>

        <div v-if="availableWorkTypes.length === 0" class="text-center py-3">
          <p class="text-xs text-gray-400">No work types available for this task</p>
        </div>

        <button
          v-for="workType in availableWorkTypes"
          :key="workType.id"
          @click="selectWorkType(workType)"
          class="w-full text-left px-3 py-2 rounded border border-gray-200 hover:border-primary-300 hover:bg-primary-50 transition-all group"
        >
          <div class="flex items-center justify-between">
            <span class="text-xs font-medium text-gray-900 group-hover:text-primary-700">{{ workType.display_name || workType.name }}</span>
            <FontAwesomeIcon :icon="['fas', 'chevron-right']" class="text-[10px] text-gray-400 group-hover:text-primary-600" />
          </div>
        </button>
      </div>

      <!-- Dynamic Form Display -->
      <div v-else class="space-y-3">
        <div class="flex items-center justify-between pb-2 border-b border-gray-100">
          <span class="text-xs font-medium text-gray-900">{{ selectedWorkType.display_name || selectedWorkType.name }}</span>
          <button v-if="!createdWorkId" @click="resetForm" class="text-xs text-primary-600 hover:text-primary-700">Change</button>
        </div>

        <!-- Post-submit ATTACHMENTS step — available for EVERY work type
             (survey / JSON-schema / generic), 50MB + full doc/pdf/ppt/img types.
             Work is created on submit, so attachments hang off the saved work id. -->
        <template v-if="createdWorkId">
          <div class="bg-green-50 border border-green-200 rounded p-2">
            <p class="text-xs text-green-600">Work logged. Add any attachments below, then Done.</p>
          </div>
          <WorkAttachments
            :work-id="createdWorkId"
            :current-user="current_user"
          />
          <div class="flex justify-end pt-2 border-t border-gray-100">
            <button @click="finishAfterAttachments" class="px-2.5 py-1 text-xs font-medium bg-gray-900 text-white rounded hover:bg-gray-800 transition-colors">Done</button>
          </div>
        </template>

        <!-- Pre-submit: the work-type form + actions (hidden once work is saved,
             when the attachments step above takes over). -->
        <template v-if="!createdWorkId">
        <!-- Custom Widget -->
        <component
          v-if="customWidget"
          :is="customWidget"
          :work-type="selectedWorkType"
          :task-id="taskId"
          :task-type-id="taskTypeId"
          :task-metadata="taskMetadata"
          :opportunities="opportunities"
          @validation-change="isFormValid = $event"
          ref="workFormWidget"
        />

        <!-- Survey Schema Form Rendering -->
        <div v-else-if="isSurveySchema" class="space-y-2">
          <!-- Progress Bar -->
          <div v-if="surveyFields.length > 1" class="flex items-center gap-2">
            <div class="flex-1 bg-gray-200 rounded-full h-1.5">
              <div class="bg-primary-500 h-1.5 rounded-full transition-all duration-300" :style="`width: ${surveyProgress}%`"></div>
            </div>
            <span class="text-xs text-gray-400">{{ completedFieldsCount }}/{{ requiredFieldsCount }}</span>
          </div>

          <!-- Survey Fields -->
          <div v-for="field in visibleSurveyFields" :key="field.id" class="rounded border border-gray-200 p-3">
            <SurveyFieldRenderer
              :field="field"
              v-model="formData[field.id]"
              :error="fieldErrors[field.id]"
              @validate="validateField(field)"
            />
          </div>
        </div>

        <!-- JSON Schema Form Rendering -->
        <div v-else-if="hasJsonSchemaConfiguration" class="space-y-2">
          <div v-for="(propSchema, propKey) in selectedWorkType.configuration.properties" :key="propKey">

            <!-- Array of objects -->
            <div v-if="propSchema.type === 'array' && propSchema.items">
              <div class="space-y-2">
                <div class="flex items-center justify-between">
                  <label class="text-xs text-gray-500">
                    {{ propSchema.title || propKey }}
                    <span v-if="isFieldRequired(selectedWorkType.configuration, propKey)" class="text-red-500">*</span>
                  </label>
                  <button @click="addArrayItem(propKey)" type="button" class="text-xs text-primary-600 hover:text-primary-700 font-medium">+ Add</button>
                </div>

                <div v-if="formData[propKey] && formData[propKey].length > 0" class="space-y-2">
                  <div v-for="(item, itemIndex) in formData[propKey]" :key="itemIndex" class="p-2.5 border border-gray-200 rounded bg-gray-50">
                    <div class="flex items-center justify-between mb-2">
                      <span class="text-xs font-medium text-gray-500">Entry {{ itemIndex + 1 }}</span>
                      <button @click="removeArrayItem(propKey, itemIndex)" type="button" class="text-xs text-red-500 hover:text-red-600">
                        <FontAwesomeIcon :icon="['fas', 'trash-can']" class="text-xs" />
                      </button>
                    </div>
                    <div class="space-y-2">
                      <div v-for="fieldKey in getFieldOrder(propSchema.items)" :key="fieldKey">
                        <div v-if="propSchema.items.properties[fieldKey]">
                          <label :for="`${propKey}_${itemIndex}_${fieldKey}`" class="block text-xs text-gray-500 mb-0.5">
                            {{ propSchema.items.properties[fieldKey].title || fieldKey }}
                            <span v-if="isFieldRequired(propSchema.items, fieldKey)" class="text-red-500">*</span>
                          </label>
                          <textarea v-if="propSchema.items.properties[fieldKey].format === 'textarea'" :id="`${propKey}_${itemIndex}_${fieldKey}`" v-model="formData[propKey][itemIndex][fieldKey]" rows="2" class="w-full px-2 py-1.5 border border-gray-300 rounded text-xs focus:ring-1 focus:ring-primary-500 focus:border-primary-500"></textarea>
                          <select v-else-if="propSchema.items.properties[fieldKey].enum" :id="`${propKey}_${itemIndex}_${fieldKey}`" v-model="formData[propKey][itemIndex][fieldKey]" class="w-full px-2 py-1.5 border border-gray-300 rounded text-xs focus:ring-1 focus:ring-primary-500 focus:border-primary-500">
                            <option value="">—</option>
                            <option v-for="option in propSchema.items.properties[fieldKey].enum" :key="option" :value="option">{{ option }}</option>
                          </select>
                          <input v-else :id="`${propKey}_${itemIndex}_${fieldKey}`" v-model="formData[propKey][itemIndex][fieldKey]" type="text" class="w-full px-2 py-1.5 border border-gray-300 rounded text-xs focus:ring-1 focus:ring-primary-500 focus:border-primary-500" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div v-else class="text-center py-2">
                  <p class="text-xs text-gray-400">No entries yet</p>
                </div>
              </div>
            </div>

            <!-- Single field -->
            <div v-else>
              <label :for="propKey" class="block text-xs text-gray-500 mb-0.5">
                {{ propSchema.title || propKey }}
                <span v-if="isFieldRequired(selectedWorkType.configuration, propKey)" class="text-red-500">*</span>
              </label>
              <textarea v-if="propSchema.format === 'textarea'" :id="propKey" v-model="formData[propKey]" rows="2" class="w-full px-2 py-1.5 border border-gray-300 rounded text-xs focus:ring-1 focus:ring-primary-500 focus:border-primary-500"></textarea>
              <select v-else-if="propSchema.enum" :id="propKey" v-model="formData[propKey]" class="w-full px-2 py-1.5 border border-gray-300 rounded text-xs focus:ring-1 focus:ring-primary-500 focus:border-primary-500">
                <option value="">—</option>
                <option v-for="option in propSchema.enum" :key="option" :value="option">{{ option }}</option>
              </select>
              <input v-else-if="propSchema.type === 'number' || propSchema.type === 'integer'" :id="propKey" v-model.number="formData[propKey]" type="number" class="w-full px-2 py-1.5 border border-gray-300 rounded text-xs focus:ring-1 focus:ring-primary-500 focus:border-primary-500" />
              <input v-else :id="propKey" v-model="formData[propKey]" type="text" class="w-full px-2 py-1.5 border border-gray-300 rounded text-xs focus:ring-1 focus:ring-primary-500 focus:border-primary-500" />
            </div>
          </div>
        </div>

        <!-- Generic Work Form (Fallback) -->
        <GenericWorkForm
          v-else
          :work-type="selectedWorkType"
          @validation-change="isFormValid = $event"
          ref="workFormWidget"
        />

        <!-- Error / Success -->
        <div v-if="error" class="bg-red-50 border border-red-200 rounded p-2">
          <p class="text-xs text-red-600">{{ error }}</p>
        </div>
        <div v-if="successMessage" class="bg-green-50 border border-green-200 rounded p-2">
          <p class="text-xs text-green-600">{{ successMessage }}</p>
        </div>

        <!-- Send to Patient Form -->
        <div v-if="showSendExternally" class="bg-blue-50 border border-blue-200 rounded p-2.5 space-y-2">
          <span class="text-xs font-medium text-blue-900">Send to patient (no login required)</span>
          <p class="text-xs text-gray-500">Patient receives an email with a tokenized link to complete this form.</p>
          <div>
            <label class="block text-xs text-gray-500 mb-0.5">Recipient name</label>
            <input v-model="externalRecipientName" type="text" class="w-full border border-gray-300 rounded px-2 py-1.5 text-xs" />
          </div>
          <div>
            <label class="block text-xs text-gray-500 mb-0.5">Email</label>
            <input v-model="externalRecipientEmail" type="email" class="w-full border border-gray-300 rounded px-2 py-1.5 text-xs" />
          </div>
          <div class="flex justify-end gap-2">
            <button @click="showSendExternally = false" class="text-xs text-gray-500 hover:text-gray-700">Cancel</button>
            <button @click="sendExternally" :disabled="submitting || !externalRecipientEmail || !externalRecipientName" class="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50">
              {{ submitting ? 'Sending...' : 'Send' }}
            </button>
          </div>
        </div>

        <!-- Form Actions -->
        <div class="flex justify-end gap-2 pt-2 border-t border-gray-100">
          <button @click="closeWorkLogger" class="px-2 py-1 text-xs text-gray-600 border border-gray-300 rounded hover:bg-gray-50 transition-colors">Cancel</button>
          <button
            v-if="!showSendExternally && canSendExternally"
            @click="showSendExternally = true"
            class="px-2 py-1 text-xs text-blue-600 border border-blue-300 rounded hover:bg-blue-50 transition-colors"
          >Send to Patient</button>
          <button @click="submitWork" :disabled="submitting || !isFormValid" class="px-2.5 py-1 text-xs font-medium bg-gray-900 text-white rounded hover:bg-gray-800 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors">
            {{ submitting ? 'Submitting...' : 'Submit' }}
          </button>
        </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { mapGetters } from "vuex";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import GenericWorkForm from './GenericWorkForm.vue';
import WorkAttachments from './WorkAttachments.vue';
import CodeSuggestionWorkForm from './CodeSuggestionWorkForm.vue';
import Suspect_QA_Form from '@/components/Portal/Task_Tables/Task_Widgets/Suspect_QA_Form.vue';
import SurveyFieldRenderer from '@/components/widgetcomponents/JSON_Editors/SurveyFieldRenderer.vue';
import { checkEnrollment } from '@/services/api/opportunities';
import { isEngagementWorkType } from '@/utils/engagementLaunch';

export default {
  name: 'WorkLogger',
  components: {
    FontAwesomeIcon,
    GenericWorkForm,
    CodeSuggestionWorkForm,
    Suspect_QA_Form,
    SurveyFieldRenderer,
    WorkAttachments,
  },
  props: {
    // NEW: prop-driven interface
    patient: { type: Object, default: null },        // { identifier, entityId }
    task: { type: Object, default: null },            // full task object
    opportunity: { type: Object, default: null },     // single opportunity

    parentWorkId: { type: Number, default: null },  // Parent work ID for child work records

    // EXISTING: kept for backward compat
    opportunities: { type: Array, default: () => [] },
    taskTypeId: { type: Number, default: null },
    taskId: { type: Number, default: null },
    taskMetadata: { type: Object, default: null },
    initialFormData: { type: Object, default: null },  // Pre-fill form fields (e.g., {quality_measure: 'colorectal_cancer_screening'})
  },
  data() {
    return {
      showForm: false,
      loading: false,
      submitting: false,
      error: null,
      successMessage: null,
      // Set to the created Work id after submit; drives the attachments step.
      createdWorkId: null,
      availableWorkTypes: [],
      selectedWorkType: null,
      isFormValid: false,
      customWidget: null,
      formData: {},
      fieldErrors: {},
      // Patient mode
      patientOpportunities: [],
      selectedOpportunityId: null,
      showOppDropdown: false,
      loadingOpportunities: false,
      // Send externally
      showSendExternally: false,
      externalRecipientName: '',
      externalRecipientEmail: '',
    };
  },
  computed: {
    ...mapGetters('auth', ['isAuthenticated', 'current_user']),

    mode() {
      if (this.patient) return 'patient';
      if (this.task) return 'task';
      if (this.opportunity) return 'opportunity';
      if (this.taskTypeId) return 'task';  // legacy fallback
      return 'task';
    },

    effectiveTaskTypeId() {
      if (this.mode === 'task') return this.task?.task_type || this.taskTypeId;
      return this.taskTypeId;
    },

    effectiveTaskId() {
      if (this.mode === 'task') return this.task?.id || this.taskId;
      return this.taskId;
    },

    effectiveOpportunities() {
      if (this.mode === 'patient') {
        const selected = this.patientOpportunities.find(o => o.id === this.selectedOpportunityId);
        return selected ? [selected] : [];
      }
      if (this.mode === 'opportunity') return [this.opportunity];
      if (this.mode === 'task') return this.task?.opportunities || this.opportunities;
      return this.opportunities;
    },

    selectedOpportunityLabel() {
      const opp = this.patientOpportunities.find(o => o.id === this.selectedOpportunityId);
      return opp?.label || '';
    },

    selectedOpportunityTaskTypeName() {
      const opp = this.patientOpportunities.find(o => o.id === this.selectedOpportunityId);
      return opp?.task_type_name || null;
    },

    canSendExternally() {
      // Shows the "Send to Patient" button when the work type has a form
      // (either legacy form_schema JSON-schema or new survey fields[] schema).
      const cfg = this.selectedWorkType?.configuration;
      if (!cfg) return false;
      if (cfg.form_schema) return true;
      if (Array.isArray(cfg.fields) && cfg.fields.length > 0) return true;
      return false;
    },

    isSurveySchema() {
      return !this.customWidget && this.selectedWorkType?.configuration?.schema_type === 'survey';
    },

    hasJsonSchemaConfiguration() {
      return !this.customWidget &&
             !this.isSurveySchema &&
             this.selectedWorkType?.configuration?.properties &&
             Object.keys(this.selectedWorkType.configuration.properties).length > 0;
    },

    surveyFields() {
      if (!this.isSurveySchema) return [];
      const fields = this.selectedWorkType.configuration.fields || [];
      return [...fields].sort((a, b) => (a.order || 0) - (b.order || 0));
    },

    visibleSurveyFields() {
      return this.surveyFields
        .filter(field => this.isFieldVisible(field))
        .map(field => {
          // In patient mode, replace static quality_measures checkbox options
          // with the patient's actual open gap opportunities so users only see
          // gaps that are actually open for this patient.
          if (
            field.id === 'quality_measures' &&
            field.type === 'checkbox' &&
            this.mode === 'patient' &&
            this.patientOpportunities.length > 0
          ) {
            const openGapOptions = this.patientOpportunities.map(opp => ({
              label: opp.label || `Opportunity ${opp.id}`,
              value: opp.label || `Opportunity ${opp.id}`,
            }));
            return { ...field, options: openGapOptions };
          }
          return field;
        });
    },

    requiredFieldsCount() {
      return this.visibleSurveyFields.filter(field => field.required).length;
    },

    completedFieldsCount() {
      return this.visibleSurveyFields.filter(field => {
        if (!field.required) return false;
        const value = this.formData[field.id];
        if (field.type === 'checkbox') {
          return Array.isArray(value) && value.length > 0;
        }
        return value !== undefined && value !== null && value !== '';
      }).length;
    },

    surveyProgress() {
      if (this.requiredFieldsCount === 0) return 100;
      return Math.round((this.completedFieldsCount / this.requiredFieldsCount) * 100);
    },
  },

  watch: {
    // Watch form data for validation (survey and JSON schema forms)
    formData: {
      deep: true,
      handler() {
        if (this.isSurveySchema || this.hasJsonSchemaConfiguration) {
          this.isFormValid = this.validateFormData();
        }
      }
    },
    selectedOpportunityId(newVal) {
      if (this.mode === 'patient' && newVal) {
        this.fetchAvailableWorkTypes();
      } else if (!newVal) {
        this.availableWorkTypes = [];
      }
    }
  },

  mounted() {
    this._closeOppDropdown = (e) => {
      if (this.showOppDropdown && this.$refs.oppDropdownContainer && !this.$refs.oppDropdownContainer.contains(e.target)) {
        this.showOppDropdown = false;
      }
    };
    document.addEventListener('click', this._closeOppDropdown, true);
  },

  beforeUnmount() {
    document.removeEventListener('click', this._closeOppDropdown, true);
  },

  methods: {
    getCustomWidget(workType) {
      const name = (workType?.name || '').toLowerCase();
      if (name.includes('encounter_review_add_code') || name.includes('code suggestion')) {
        return 'CodeSuggestionWorkForm';
      }
      if (name.includes('code_qa') || name.includes('qa review')) {
        return 'Suspect_QA_Form';
      }
      return null;
    },

    getFormTypeDescription() {
      if (this.customWidget) return 'Complete the custom form below';
      if (this.isSurveySchema) return 'Complete the survey below';
      if (this.hasJsonSchemaConfiguration) return 'Complete the fields below';
      return 'Complete the form below';
    },

    // Survey-specific methods
    isFieldVisible(field) {
      if (!field.condition) return true;

      const condition = field.condition;
      const rules = condition.rules || [];

      if (rules.length === 0) return true;

      const results = rules.map(rule => this.evaluateConditionRule(rule));

      const meetsCondition = condition.logic === 'OR'
        ? results.some(r => r)
        : results.every(r => r);

      return condition.type === 'show_if' ? meetsCondition : !meetsCondition;
    },

    evaluateConditionRule(rule) {
      const fieldValue = this.formData[rule.field];
      const compareValue = rule.value;

      switch (rule.operator) {
        case 'equals':
          return fieldValue === compareValue;
        case 'not_equals':
          return fieldValue !== compareValue;
        case 'greater_than':
          return Number(fieldValue) > Number(compareValue);
        case 'less_than':
          return Number(fieldValue) < Number(compareValue);
        case 'contains':
          return String(fieldValue).includes(String(compareValue));
        case 'in':
          return Array.isArray(compareValue) && compareValue.includes(fieldValue);
        case 'not_empty':
          return fieldValue !== null && fieldValue !== undefined && fieldValue !== '';
        case 'is_empty':
          return fieldValue === null || fieldValue === undefined || fieldValue === '';
        default:
          return true;
      }
    },

    validateField(field) {
      delete this.fieldErrors[field.id];

      const value = this.formData[field.id];

      if (field.required && (value === null || value === undefined || value === '')) {
        this.fieldErrors[field.id] = 'This field is required';
        return false;
      }

      if (field.validation && value !== null && value !== undefined && value !== '') {
        const validation = field.validation;

        if (field.type === 'number') {
          const numValue = Number(value);
          if (validation.min !== undefined && numValue < validation.min) {
            this.fieldErrors[field.id] = validation.customMessage || `Value must be at least ${validation.min}`;
            return false;
          }
          if (validation.max !== undefined && numValue > validation.max) {
            this.fieldErrors[field.id] = validation.customMessage || `Value must be at most ${validation.max}`;
            return false;
          }
        }

        if (field.type === 'text' || field.type === 'textarea') {
          const strValue = String(value);
          if (validation.minLength !== undefined && strValue.length < validation.minLength) {
            this.fieldErrors[field.id] = validation.customMessage || `Must be at least ${validation.minLength} characters`;
            return false;
          }
          if (validation.maxLength !== undefined && strValue.length > validation.maxLength) {
            this.fieldErrors[field.id] = validation.customMessage || `Must be at most ${validation.maxLength} characters`;
            return false;
          }
          if (validation.pattern) {
            const regex = new RegExp(validation.pattern);
            if (!regex.test(strValue)) {
              this.fieldErrors[field.id] = validation.customMessage || 'Invalid format';
              return false;
            }
          }
        }
      }

      this.fieldErrors = { ...this.fieldErrors };
      return true;
    },

    validateFormData() {
      if (this.isSurveySchema) {
        for (const field of this.visibleSurveyFields) {
          if (field.type === 'nested_list') {
            const entries = this.formData[field.id];
            if (field.required && (!Array.isArray(entries) || entries.length === 0)) {
              return false;
            }
            if (Array.isArray(entries)) {
              for (const entry of entries) {
                for (const child of (field.fields || [])) {
                  if (!child.required) continue;
                  const childValue = entry ? entry[child.id] : undefined;
                  if (child.type === 'checkbox') {
                    if (!Array.isArray(childValue) || childValue.length === 0) return false;
                  } else if (childValue === undefined || childValue === null || childValue === '') {
                    return false;
                  }
                }
              }
            }
            continue;
          }
          if (field.required) {
            const value = this.formData[field.id];
            if (field.type === 'checkbox') {
              if (!Array.isArray(value) || value.length === 0) return false;
            } else if (value === undefined || value === null || value === '') {
              return false;
            }
          }
        }
        return true;
      } else if (this.hasJsonSchemaConfiguration) {
        const config = this.selectedWorkType.configuration;

        if (config.required) {
          for (const requiredField of config.required) {
            const value = this.formData[requiredField];

            if (Array.isArray(value)) {
              if (value.length === 0) return false;

              const propSchema = config.properties[requiredField];
              if (propSchema?.items?.required) {
                for (const item of value) {
                  for (const itemRequiredField of propSchema.items.required) {
                    if (!item[itemRequiredField] || item[itemRequiredField] === '') {
                      return false;
                    }
                  }
                }
              }
            } else {
              if (value === undefined || value === null || value === '') {
                return false;
              }
            }
          }
        }
        return true;
      }
      return false;
    },

    initializeSurveyFormData() {
      if (!this.isSurveySchema) return;

      const fields = this.selectedWorkType.configuration.fields || [];

      fields.forEach(field => {
        // Apply initial value from initialFormData if provided
        if (this.initialFormData && this.initialFormData[field.id] !== undefined) {
          this.formData[field.id] = this.initialFormData[field.id];
        } else if (field.type === 'checkbox') {
          this.formData[field.id] = [];
        } else if (field.type === 'nested_list') {
          this.formData[field.id] = [this.buildNestedEntry(field)];
        } else if (field.type === 'number' || field.type === 'boolean' || field.type === 'scale') {
          this.formData[field.id] = null;
        } else if (field.type === 'select' || field.type === 'radio' || field.type === 'date') {
          this.formData[field.id] = '';
        } else {
          this.formData[field.id] = '';
        }
      });
    },

    buildNestedEntry(field) {
      const entry = {};
      (field.fields || []).forEach(child => {
        if (child.type === 'checkbox') {
          entry[child.id] = [];
        } else if (child.type === 'number' || child.type === 'boolean' || child.type === 'scale') {
          entry[child.id] = null;
        } else {
          entry[child.id] = '';
        }
      });
      return entry;
    },

    initializeJsonSchemaFormData() {
      if (!this.selectedWorkType?.configuration?.properties) return;

      const config = this.selectedWorkType.configuration;

      Object.keys(config.properties).forEach(key => {
        const prop = config.properties[key];

        if (prop.type === 'array') {
          this.formData[key] = [];
          if (config.required?.includes(key)) {
            this.addArrayItem(key);
          }
        } else if (prop.type === 'object') {
          this.formData[key] = {};
          if (prop.properties) {
            Object.keys(prop.properties).forEach(subKey => {
              this.formData[key][subKey] = this.getDefaultValue(prop.properties[subKey]);
            });
          }
        } else {
          this.formData[key] = this.getDefaultValue(prop);
        }
      });
    },

    getDefaultValue(schema) {
      if (schema.default !== undefined) return schema.default;
      if (schema.enum && schema.enum.length > 0) return '';
      if (schema.type === 'string') return '';
      if (schema.type === 'number' || schema.type === 'integer') return null;
      if (schema.type === 'boolean') return false;
      if (schema.type === 'array') return [];
      if (schema.type === 'object') return {};
      return '';
    },

    createEmptyArrayItem(itemSchema) {
      const item = {};
      if (itemSchema.properties) {
        Object.keys(itemSchema.properties).forEach(key => {
          item[key] = this.getDefaultValue(itemSchema.properties[key]);
        });
      }
      return item;
    },

    addArrayItem(propKey) {
      if (!this.formData[propKey]) {
        this.formData[propKey] = [];
      }

      const propSchema = this.selectedWorkType.configuration.properties[propKey];
      if (propSchema?.items) {
        const newItem = this.createEmptyArrayItem(propSchema.items);
        this.formData[propKey].push(newItem);
      }
    },

    removeArrayItem(propKey, index) {
      if (this.formData[propKey] && Array.isArray(this.formData[propKey])) {
        this.formData[propKey].splice(index, 1);
      }
    },

    getFieldOrder(itemSchema) {
      if (itemSchema['ui:order'] && Array.isArray(itemSchema['ui:order'])) {
        return itemSchema['ui:order'];
      }
      return Object.keys(itemSchema.properties || {});
    },

    isFieldRequired(schema, fieldKey) {
      return schema.required && Array.isArray(schema.required) && schema.required.includes(fieldKey);
    },

    async openWorkLogger() {
      this.showForm = true;
      this.error = null;
      if (this.mode === 'patient') {
        await this.fetchPatientOpportunities();
      } else {
        await this.fetchAvailableWorkTypes();
      }
    },

    closeWorkLogger() {
      this.showForm = false;
      this.selectedWorkType = null;
      this.error = null;
      this.successMessage = null;
      this.customWidget = null;
      this.isFormValid = false;
      this.formData = {};
      this.fieldErrors = {};
      this.selectedOpportunityId = null;
      this.showOppDropdown = false;
      this.availableWorkTypes = [];
      this.createdWorkId = null;
    },

    // Called from the post-submit attachments step's "Done" button.
    finishAfterAttachments() {
      this.closeWorkLogger();
    },

    resetForm() {
      this.selectedWorkType = null;
      this.customWidget = null;
      this.formData = {};
      this.fieldErrors = {};
      this.isFormValid = false;
      this.createdWorkId = null;
    },

    isInlineOnlyWorkType(wt) {
      // Code QA and Coder Response are inline-on-the-code-card flows
      // submitted from CodeSuggestionDisplay with a parent FK to the
      // original Add Code work item. They are not valid free-floating
      // entries from the generic WorkLogger dropdown.
      const name = (wt?.name || '').toLowerCase();
      return name.includes('code_qa') || name.includes('qa review')
        || name.includes('coder_response') || name.includes('coder response');
    },

    async fetchAvailableWorkTypes() {
      this.loading = true;
      try {
        let taskTypeId;

        if (this.mode === 'patient') {
          const opp = this.patientOpportunities.find(o => o.id === this.selectedOpportunityId);
          taskTypeId = opp?.task_type_id;
          if (!taskTypeId) {
            this.availableWorkTypes = [];
            return;
          }
        } else if (this.mode === 'opportunity') {
          taskTypeId = this.opportunity.task_type_id || this.effectiveTaskTypeId;
        } else {
          taskTypeId = this.effectiveTaskTypeId;
        }

        const response = await axios.get('/api/workassignments/', {
          params: { task_type: taskTypeId }
        });
        this.availableWorkTypes = response.data
          .map(wa => wa.work_type_details || wa.work_type)
          .filter(wt => !this.isInlineOnlyWorkType(wt) && !isEngagementWorkType(wt));
      } catch (error) {
        console.error('Error fetching work types:', error);
        this.error = 'Failed to load work types. Please try again.';
      } finally {
        this.loading = false;
      }
    },

    async fetchPatientOpportunities() {
      if (!this.patient) return;
      this.loadingOpportunities = true;
      try {
        // Fetch active opportunities PLUS recently-completed ones. A nurse must
        // be able to log a final note (e.g. provider communication) against an
        // opportunity she just closed (req 1868); the backend allows logging
        // work on a completed opp, it was only hidden from this picker.
        const enrollmentResponse = await checkEnrollment({
          patient_identifier: this.patient.identifier,
          show_completed: true,
        });
        const enrollments = enrollmentResponse.enrolled_programs || [];
        const RECENT_MS = 30 * 24 * 60 * 60 * 1000; // completed within 30 days
        const now = Date.now();
        this.patientOpportunities = enrollments
          .filter(e => {
            if (!e.completed_at) {
              // active opp: keep if it has open tasks (or is task-less)
              return e.active_task_count > 0 || e.tasks.length === 0;
            }
            // completed opp: keep only if recently closed
            return (now - new Date(e.completed_at).getTime()) <= RECENT_MS;
          })
          .map(e => {
            const activeTask = (e.tasks || []).find(t => !t.is_inactive);
            const firstTask = (e.tasks || [])[0];
            const task = activeTask || firstTask;
            return {
              id: e.opportunity_id,
              metric_id: e.metric_id,
              label: e.completed_at ? `${e.metric_name} (completed)` : e.metric_name,
              entity_id: e.entity_id,
              completed: !!e.completed_at,
              task_type_id: task?.task_type?.id || null,
              task_id: activeTask?.id || null,
              task_type_name: task?.task_type?.name || null,
            };
          });
      } catch (error) {
        console.error('Error fetching patient opportunities:', error);
        this.error = 'Failed to load opportunities.';
      } finally {
        this.loadingOpportunities = false;
      }
    },

    selectWorkType(workType) {
      this.selectedWorkType = workType;
      this.error = null;
      this.formData = {};
      this.fieldErrors = {};

      const customWidgetName = this.getCustomWidget(workType);
      if (customWidgetName && this.$options.components[customWidgetName]) {
        this.customWidget = customWidgetName;
      } else {
        this.customWidget = null;

        if (this.isSurveySchema) {
          this.initializeSurveyFormData();
        } else if (this.hasJsonSchemaConfiguration) {
          this.initializeJsonSchemaFormData();
        }
      }
    },

    async submitWork() {
      let submissionData = {};

      // Get data from custom widget
      if (this.customWidget && this.$refs.workFormWidget && typeof this.$refs.workFormWidget.getFormData === 'function') {
        submissionData = this.$refs.workFormWidget.getFormData();
      }
      // Get data from GenericWorkForm
      else if (!this.isSurveySchema && !this.hasJsonSchemaConfiguration && this.$refs.workFormWidget && typeof this.$refs.workFormWidget.getFormData === 'function') {
        submissionData = this.$refs.workFormWidget.getFormData();
      }
      // Use inline form data (survey or JSON schema)
      else {
        submissionData = { ...this.formData };
      }

      this.submitting = true;
      this.error = null;

      try {
        // Determine opportunity_ids and entity based on mode
        let opportunityIds;
        let entityId;
        let taskId;

        if (this.mode === 'patient') {
          opportunityIds = this.selectedOpportunityId ? [this.selectedOpportunityId] : [];
          entityId = this.patient.entityId || this.effectiveOpportunities[0]?.entity_id;
          taskId = null;
        } else if (this.mode === 'opportunity') {
          opportunityIds = [this.opportunity.id];
          entityId = this.opportunity.entity || this.opportunity.entity_id;
          taskId = this.opportunity.task_id || this.effectiveTaskId;
        } else {
          const opps = this.task?.opportunities || this.opportunities;
          opportunityIds = opps.map(opp => opp.id);
          entityId = opps[0]?.entity;
          taskId = this.effectiveTaskId;
        }

        const workData = {
          opportunity_ids: opportunityIds,
          opportunity: opportunityIds[0] || null,  // DEPRECATED: kept for backward compatibility
          work_type: this.selectedWorkType.id,
          appuser: this.current_user.id,
          task: taskId || null,
          entity: entityId,
          parent: this.parentWorkId || null,
          metadata: {
            ...submissionData,
            timestamp: new Date().toISOString()
          }
        };

        const resp = await axios.post('/api/work/', workData);

        // Tell listeners the work was logged (grids refresh), but DON'T close yet —
        // reveal the attachments step so the user can add documents to the saved
        // work (any work type). Closing happens on "Done".
        this.$emit('work-submitted');
        this.createdWorkId = resp?.data?.id || null;
        if (!this.createdWorkId) {
          // Fallback: no id returned (shouldn't happen) — close as before.
          this.successMessage = 'Work successfully logged!';
          setTimeout(() => { this.closeWorkLogger(); this.successMessage = null; }, 1500);
        }

      } catch (error) {
        console.error('Error submitting work:', error);
        this.error = error.response?.data?.detail || 'Failed to submit. Please try again.';
      } finally {
        this.submitting = false;
      }
    },

    async sendExternally() {
      this.submitting = true;
      this.error = null;

      try {
        // Determine entity and opportunity context
        let opportunityIds;
        let entityId;
        let taskId;

        if (this.mode === 'patient') {
          opportunityIds = this.selectedOpportunityId ? [this.selectedOpportunityId] : [];
          entityId = this.patient.entityId || this.effectiveOpportunities[0]?.entity_id;
          taskId = null;
        } else if (this.mode === 'opportunity') {
          opportunityIds = [this.opportunity.id];
          entityId = this.opportunity.entity || this.opportunity.entity_id;
          taskId = this.opportunity.task_id || this.effectiveTaskId;
        } else {
          const opps = this.task?.opportunities || this.opportunities;
          opportunityIds = opps.map(opp => opp.id);
          entityId = opps[0]?.entity;
          taskId = this.effectiveTaskId;
        }

        // 1. Create the Work record (empty metadata — recipient will fill it)
        const workResponse = await axios.post('/api/work/', {
          opportunity_ids: opportunityIds,
          opportunity: opportunityIds[0] || null,
          work_type: this.selectedWorkType.id,
          appuser: this.current_user.id,
          task: taskId || null,
          entity: entityId,
          metadata: {},
        });

        // 2. Create AsyncEngagement and send email
        const engagementResponse = await axios.post('/api/outreach/async-engagements/', {
          title: this.selectedWorkType.display_name || this.selectedWorkType.name,
          description: '',
          recipient_name: this.externalRecipientName,
          recipient_email: this.externalRecipientEmail,
          expires_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
          work_items: [workResponse.data.id],
        });

        this.successMessage = `Sent to ${this.externalRecipientName}`;
        this.showSendExternally = false;
        this.$emit('work-submitted');

        setTimeout(() => {
          this.closeWorkLogger();
          this.successMessage = null;
        }, 2000);

      } catch (error) {
        console.error('Error sending externally:', error);
        this.error = error.response?.data?.detail || 'Failed to send. Please try again.';
      } finally {
        this.submitting = false;
      }
    }
  }
};
</script>

<style scoped>
.animate-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
